import { LogOut, User, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/auth/auth-context";
import { useTenant } from "@/tenant/tenant-context";
import { useHealth } from "@/api/hooks/use-health";
import { cn } from "@/lib/utils";

/**
 * Application top header bar.
 *
 * Displays the current tenant ID, system health indicator, and a user
 * dropdown menu with logout functionality.
 */
export function Header() {
  const { user, logout } = useAuth();
  const { tenantId } = useTenant();
  const { data: health } = useHealth();

  const isHealthy = health?.status === "healthy";
  const healthColor = health == null ? "bg-gray-400" : isHealthy ? "bg-green-500" : "bg-red-500";

  return (
    <header className="flex h-16 items-center justify-between border-b bg-card px-6">
      {/* Left: tenant context */}
      <div className="flex items-center gap-3">
        {tenantId && (
          <div className="flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs text-muted-foreground">
            <span className="font-medium">Tenant:</span>
            <span className="font-mono">{tenantId}</span>
          </div>
        )}
      </div>

      {/* Right: health + user menu */}
      <div className="flex items-center gap-4">
        {/* System health indicator */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span
            className={cn("h-2 w-2 rounded-full", healthColor)}
            title={health == null ? "Checking health…" : isHealthy ? "All systems healthy" : "System degraded"}
          />
          <span className="hidden sm:inline">
            {health == null ? "Checking…" : isHealthy ? "Healthy" : "Degraded"}
          </span>
        </div>

        {/* User dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2">
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">{user?.actor_id ?? "User"}</span>
              <ChevronDown className="h-3 w-3 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium">{user?.actor_id ?? "Unknown"}</p>
                {user?.scopes && user.scopes.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {user.scopes.join(", ")}
                  </p>
                )}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
